import { useCallback, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Pressable, StyleSheet, View } from 'react-native';

import {
  BodyText,
  capitalizeFirstLetter,
  Icon,
  theme,
  usePressableState,
  webOnlyStyles,
} from '@sg/garnish';

import type { AddressDetails, AddressWithoutIds, LocationAddress } from '../../../locations.types';

export const LocationSearchAddress = (props: LocationSearchAddressProps) => {
  const { variation, onPress, addressDetails: customerAddressDetails } = props;

  const { name, address, latitude, longitude, street, city, state, zipCode, country } =
    props.address;

  const { formatMessage } = useIntl();

  const fullAddress = name ? `${capitalizeFirstLetter(name)} · ${address}` : address;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const onAddressBtnPress = useCallback(() => {
    if (variation === 'customer-address') {
      onPress(customerAddressDetails);

      return;
    }

    onPress({
      latitude,
      longitude,
      street,
      city,
      state,
      country,
      zipCode,
      formattedAddress: address,
    });
  }, [
    variation,
    onPress,
    latitude,
    longitude,
    street,
    city,
    state,
    country,
    zipCode,
    address,
    customerAddressDetails,
  ]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const pressableRef = useRef<View>(null);
  const { isHovered, isFocused, isActive } = usePressableState(
    pressableRef as React.RefObject<HTMLElement | null>,
  );

  const pressableStyles = [
    styles.addressPressable,
    isHovered || isFocused ? styles.addressPressableHover : undefined,
    isActive ? styles.addressPressableActive : undefined,
    addressPressableWebStyles,
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Pressable
      ref={pressableRef}
      accessibilityRole="button"
      accessibilityHint={formatMessage(messages.addressA11yHint)}
      accessibilityLabel={formatMessage(messages.addressA11yLabel, {
        address,
      })}
      style={pressableStyles}
      onPress={onAddressBtnPress}
    >
      <View>
        <LocationSearchAddressIcon name={name} />
      </View>

      <View style={styles.addressPressableContent} pointerEvents="none">
        <BodyText selectable={false} numberOfLines={2}>
          {fullAddress}
        </BodyText>
      </View>

      <View>
        <Icon name="IconCaretRight" width={16} height={16} />
      </View>
    </Pressable>
  );
};

const LocationSearchAddressIcon = (props: Pick<LocationAddress, 'name'>) => {
  const { name } = props;

  if (name === 'home') return <Icon name="IconHomeStrokeSm" />;

  if (name === 'work') return <Icon name="IconWorkStrokeSm" />;

  return <Icon name="IconCustomStrokeSm" />;
};

// ─── Messages ───────────────────────────────────────────────────────────────────
const messages = defineMessages({
  addressA11yHint: {
    defaultMessage: 'Selects address',
    description: 'Location Search | Search Result | A11y hint | Selects address',
  },
  addressA11yLabel: {
    defaultMessage: 'Select {address} address',
    description: 'Location search | Search result | A11y label | Selects address',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  addressPressable: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: theme.spacing['2'],
    paddingVertical: theme.spacing['4'],
    borderRadius: theme.radius.small,
  },
  addressPressableHover: {
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
  },
  addressPressableActive: {
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  addressPressableContent: {
    flex: 1,
    marginHorizontal: theme.spacing['4'],
  },
});

const addressPressableWebStyles = webOnlyStyles({
  transition: `background-color ${theme.transitions.base}ms`,
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationSearchAddressProps =
  | {
      variation: 'customer-address';
      address: LocationAddress;
      addressDetails: AddressDetails;
      onPress: (addressDetails: AddressDetails) => void;
    }
  | {
      variation: 'prediction-address';
      address: LocationAddress;
      addressDetails?: never;
      onPress: (addressDetails: AddressWithoutIds) => void;
    };
